import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Layout from '../components/layout/Layout';
import { ProjectsList } from '../components/dashboard';
import FolderSidebar from '../components/dashboard/FolderSidebar';
import { getTheme } from '../utils/branding';
import { NewStudyModal } from '../components/modals';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Pagination from '../components/common/Pagination';
import Toast from '../components/common/Toast';
import Icon from '../components/common/Icon';
import { studiesAPI, foldersAPI } from '../services/api';
import { POLLING_INTERVAL } from '../utils/constants';
import { getCssVar } from '../utils/cssVar';
import authService from '../services/authService';
import './Dashboard.css';

const Dashboard = () => {
    const [loading, setLoading] = useState(true);
    const [projects, setProjects] = useState([]);
    const [folderProjects, setFolderProjects] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [folders, setFolders] = useState([]);
    const [studyFolderMap, setStudyFolderMap] = useState({});
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [toast, setToast] = useState(null);
    const pollingIntervalId = useRef(null);
    const currentPageRef = useRef(currentPage);
    const iconGrayColor = useMemo(() => getCssVar('--color-gray-200'), []);
    const iconWhiteColor = useMemo(() => getCssVar('--color-white'), []);
    const searchTermRef = useRef(searchTerm);
    const toolbarDebounceRef = useRef(null);
    const isSuperAdmin = authService.isSuperAdmin();
    const theme = getTheme();
    const isAdsup = theme === 'adsup';
    const PAGE_SIZE = 20;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    const fetchStudyFolderMap = useCallback(async () => {
        try {
            const foldersData = await foldersAPI.list();
            const foldersArray = Array.isArray(foldersData) ? foldersData : [];
            setFolders(foldersArray);

            const map = {};
            foldersArray.forEach(folder => {
                (folder.studyIds || []).forEach(studyId => {
                    map[studyId] = folder.folderId;
                });
            });
            setStudyFolderMap(map);
        } catch (err) {
            console.error('Erreur chargement dossiers:', err);
        }
    }, []);

    const fetchDashboardData = useCallback(async (showLoadingSpinner = false, page = 1, search = '') => {
        try {
            if (showLoadingSpinner) {
                setLoading(true);
            }
            setError(null);

            const skip = (page - 1) * PAGE_SIZE;
            const response = await studiesAPI.list({ skip, take: PAGE_SIZE, search });

            const projectsArray = Array.isArray(response.items) ? response.items : [];
            setTotalCount(response.totalCount || 0);
            setCurrentPage(page);

            const transformedProjects = projectsArray.map((study) => ({
                studyId: study.studyId,
                name: study.name || `Analyse ${study.clientDomain}`,
                domain: study.clientDomain,
                keywordsCount: study.totalKeywords || 0,
                completionPercentage: study.completionPercentage || 0,
                status: study.status,
                createdAt: study.createdAt,
                errorMessage: study.errorMessage,
                errorType: study.errorType,
                userDisplayName: study.userDisplayName,
                hasPendingSnapshot: study.hasPendingSnapshot || false,
                pendingSnapshotStatus: study.pendingSnapshotStatus,
                languageId: study.languageId,
                geoTarget: study.geoTargetConstant
            }));

            setProjects(transformedProjects);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err.message);
            setProjects([]);
        } finally {
            if (showLoadingSpinner) {
                setLoading(false);
            }
        }
    }, []);

    const fetchFolderStudies = useCallback(async (folderId) => {
        if (!folderId || folderId === 'unassigned') return;
        try {
            setLoading(true);
            const studiesData = await foldersAPI.getStudies(folderId);
            const studiesArray = Array.isArray(studiesData) ? studiesData : [];
            const transformedProjects = studiesArray.map((study) => ({
                studyId: study.studyId,
                name: study.name || `Analyse ${study.clientDomain}`,
                domain: study.clientDomain,
                keywordsCount: study.totalKeywords || 0,
                completionPercentage: study.completionPercentage || 0,
                status: study.status,
                createdAt: study.createdAt,
                errorMessage: study.errorMessage,
                errorType: study.errorType,
                userDisplayName: study.userDisplayName,
                hasPendingSnapshot: study.hasPendingSnapshot || false,
                pendingSnapshotStatus: study.pendingSnapshotStatus,
                languageId: study.languageId,
                geoTarget: study.geoTargetConstant
            }));
            setFolderProjects(transformedProjects);
        } catch (err) {
            console.error('Error fetching folder studies:', err);
            setFolderProjects([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelectFolder = useCallback((folderId) => {
        setSelectedFolderId(folderId);
        setSearchTerm('');
        searchTermRef.current = '';
        if (folderId && folderId !== 'unassigned') {
            fetchFolderStudies(folderId);
        }
    }, [fetchFolderStudies]);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            currentPageRef.current = page;
            fetchDashboardData(true, page, searchTermRef.current);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };


    // Fetch dashboard data on mount
    useEffect(() => {
        fetchDashboardData(true);
        fetchStudyFolderMap();
    }, [fetchDashboardData, fetchStudyFolderMap]);

    // Setup polling when projects change (separate effect)
    useEffect(() => {
        // Clear existing interval
        if (pollingIntervalId.current) {
            clearInterval(pollingIntervalId.current);
            pollingIntervalId.current = null;
        }

        // Check if we have pending studies
        const hasPendingStudies = projects.some(
            p => p.status === 'Pending' || 
                 p.status === 'AuthoritasInProgress' || 
                 p.status === 'AuthoritasCompleted' ||
                 p.status === 'GoogleAdsInProgress' ||
                 p.status === 'Computing'
        );

        if (hasPendingStudies) {
            console.log('🔄 Starting polling for study updates...');
            pollingIntervalId.current = setInterval(() => {
                console.log('⏰ Polling tick - fetching updates...');
                fetchDashboardData(false, currentPageRef.current, searchTermRef.current);
            }, POLLING_INTERVAL);
        } else {
            console.log('✅ No pending studies - polling stopped');
        }

        // Cleanup on unmount or when dependencies change
        return () => {
            if (pollingIntervalId.current) {
                clearInterval(pollingIntervalId.current);
                pollingIntervalId.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]); // Re-run when projects change (fetchDashboardData is stable with no deps)

    const handleCreateProject = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSubmitNewStudy = async (studyData) => {
        try {
            await studiesAPI.create(studyData);
            
            // Show success toast
            setToast({
                message: `Projet "${studyData.domain}" créé avec succès ! L'analyse va commencer sous peu.`,
                type: 'success'
            });
            
            // Refresh dashboard data to show the new study
            await fetchDashboardData();
            
            // Stay on dashboard - don't navigate to project page
            // The project will appear in the list with its status
            // User can click on it when ready to view details
            
        } catch (error) {
            console.error('Error creating study:', error);
            
            // Show error toast
            setToast({
                message: error.message || 'Erreur lors de la création du projet',
                type: 'error'
            });
        }
    };

    const handleRetrySuccess = async (studyId) => {
        setToast({
            message: 'Étude relancée avec succès ! L\'analyse va redémarrer sous peu.',
            type: 'success'
        });
        await fetchDashboardData();
    };

    const handleCancelSuccess = async (studyId) => {
        setToast({
            message: 'Analyse annulée.',
            type: 'success'
        });
        await fetchDashboardData(false, currentPageRef.current, searchTermRef.current);
    };

    const handleFolderChange = async () => {
        await fetchStudyFolderMap();
        if (selectedFolderId && selectedFolderId !== 'unassigned') {
            await fetchFolderStudies(selectedFolderId);
        }
    };

    const filteredProjects = (() => {
        if (selectedFolderId === null) return projects;
        if (selectedFolderId === 'unassigned') {
            return projects.filter(p => !studyFolderMap[p.studyId]);
        }
        return folderProjects;
    })();

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        searchTermRef.current = term;
        setCurrentPage(1);
        currentPageRef.current = 1;
        fetchDashboardData(true, 1, term);
    }, [fetchDashboardData]);

    if (loading) {
        return (
            <Layout>
                <div className="dashboard-loading">
                    <LoadingSpinner size="lg" text="Chargement du tableau de bord..." />
                </div>
            </Layout>
        );
    }

    const handleToolbarSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        searchTermRef.current = value;

        if (toolbarDebounceRef.current) {
            clearTimeout(toolbarDebounceRef.current);
        }
        toolbarDebounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            currentPageRef.current = 1;
            fetchDashboardData(true, 1, value);
        }, 300);
    };

    const handleToolbarClearSearch = () => {
        setSearchTerm('');
        searchTermRef.current = '';
        setCurrentPage(1);
        currentPageRef.current = 1;
        fetchDashboardData(true, 1, '');
    };

    return (
        <Layout>
            <div className="dashboard">
                {error && (
                    <div className="dashboard-error">
                        <p>Une erreur est survenue : {error}</p>
                        <button onClick={fetchDashboardData}>Réessayer</button>
                    </div>
                )}

                {isSuperAdmin && (
                    <div className="superadmin-banner">
                        <span className="superadmin-badge">SuperAdmin</span>
                        <span className="superadmin-info">
                            Vous voyez toutes les études de toutes les agences
                        </span>
                    </div>
                )}

                {isAdsup && (
                    <div className="dashboard-toolbar">
                        <div className="toolbar-search">
                            <input
                                type="text"
                                className="toolbar-search-input"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleToolbarSearchChange}
                            />
                            {searchTerm ? (
                                <button
                                    className="toolbar-search-clear"
                                    onClick={handleToolbarClearSearch}
                                    aria-label="Effacer la recherche"
                                >
                                    <Icon name="X" size={16} color={iconGrayColor} />
                                </button>
                            ) : (
                                <Icon name="Search" size={18} color={iconGrayColor} />
                            )}
                        </div>
                        <button className="toolbar-create-btn" onClick={handleCreateProject}>
                            <span className="toolbar-create-icon">
                                <Icon name="Plus" size={16} color={iconWhiteColor} />
                            </span>
                            Create project
                        </button>
                    </div>
                )}

                <div className="dashboard-content">
                    <FolderSidebar
                        folders={folders}
                        selectedFolderId={selectedFolderId}
                        onSelectFolder={handleSelectFolder}
                        onFoldersChange={fetchStudyFolderMap}
                    />

                    <div className="dashboard-main">
                        <ProjectsList
                            projects={filteredProjects}
                            onCreateProject={handleCreateProject}
                            onRetrySuccess={handleRetrySuccess}
                            onCancelSuccess={handleCancelSuccess}
                            folders={folders}
                            studyFolderMap={studyFolderMap}
                            onFolderChange={handleFolderChange}
                            onSearch={!isAdsup && selectedFolderId === null ? handleSearch : undefined}
                            searchTerm={!isAdsup && selectedFolderId === null ? searchTerm : ''}
                            totalCount={selectedFolderId === null ? totalCount : filteredProjects.length}
                            showHeader={!isAdsup}
                        />

                        {selectedFolderId === null && totalPages > 1 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        )}
                    </div>
                </div>

                <NewStudyModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSubmit={handleSubmitNewStudy}
                />

                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        </Layout>
    );
};

export default Dashboard;
